/** Crypto payment state shared by the payment screen, the deposit ticket and the dashboard card. */
import { useEffect, useState } from 'react';
import type { CryptoPayment } from '@platform/shared';
import { api } from '../../../lib/api';
import { useVersion } from '../../../stores/account';
import { AIcon, errMsg, Notice, PillButton } from '../kit';

export const OPEN: CryptoPayment['status'][] = ['waiting', 'confirming', 'confirmed', 'sending'];
export const STATUS: Record<CryptoPayment['status'], [string, 'warn' | 'accent' | 'up' | 'down' | 'muted']> = {
  created: ['Starting', 'muted'],
  waiting: ['Waiting for payment', 'warn'],
  confirming: ['Confirming', 'accent'],
  confirmed: ['Confirming', 'accent'],
  sending: ['Confirming', 'accent'],
  partially_paid: ['Partly paid', 'warn'],
  finished: ['Credited', 'up'],
  failed: ['Failed', 'down'],
  refunded: ['Refunded', 'muted'],
  expired: ['Expired', 'muted'],
  cancelled: ['Cancelled', 'muted'],
};

/** Still payable: open, and not a waiting payment whose 45 minutes have run out. */
export const resumable = (p: CryptoPayment) => OPEN.includes(p.status) && (!p.expiresAt || Date.parse(p.expiresAt) > Date.now() || p.status !== 'waiting');

/** Keep `active` current: live events bump the deposit version; a slow poll covers a dropped socket. */
export function useFollowPayment(active: CryptoPayment | null, setActive: (f: (a: CryptoPayment | null) => CryptoPayment | null) => void) {
  const v = useVersion('deposit');
  useEffect(() => {
    if (!active) return;
    let gone = false;
    const load = () =>
      api
        .get<CryptoPayment>(`/api/deposits/crypto/${active.id}`)
        .then((p) => !gone && setActive((a) => (a && a.id === p.id ? p : a)))
        .catch(() => {});
    void load();
    const id = OPEN.includes(active.status) ? setInterval(load, 15_000) : null;
    return () => {
      gone = true;
      if (id) clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id, active?.status, v]);
}

export function useNow(active: boolean) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

/** Milliseconds as mm:ss. */
export const mmss = (ms: number) => `${String(Math.floor(ms / 60000)).padStart(2, '0')}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, '0')}`;

/** "Cancel payment" with a confirm step, for a payment still waiting for coins. */
export function CancelPayment({ p, onDone }: { p: CryptoPayment; onDone: (p: CryptoPayment) => void }) {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const cancel = async () => {
    setBusy(true);
    setErr(null);
    try {
      onDone(await api.post<CryptoPayment>(`/api/deposits/crypto/${p.id}/cancel`));
    } catch (x) {
      setErr(errMsg(x));
      setAsking(false);
    } finally {
      setBusy(false);
    }
  };
  if (p.status !== 'waiting') return null;
  return (
    <>
      {err && <Notice msg={{ ok: false, text: err }} onClose={() => setErr(null)} />}
      {asking ? (
        <div className="flex w-full flex-col gap-3 rounded-[16px] border border-line-strong p-4">
          <b className="text-[15px]">Cancel this payment?</b>
          <span className="text-[14px] text-muted">Don't send coins to this address after cancelling. If some are already on the way, they are still credited when they arrive.</span>
          <div className="flex flex-wrap gap-2">
            <PillButton size="sm" kind="destructive" busy={busy} onClick={() => void cancel()}>
              Cancel payment
            </PillButton>
            <PillButton size="sm" kind="secondary" onClick={() => setAsking(false)}>
              Keep it
            </PillButton>
          </div>
        </div>
      ) : (
        <PillButton size="sm" kind="destructive" className="self-start" onClick={() => setAsking(true)}>
          <AIcon name="x" size={16} />
          Cancel payment
        </PillButton>
      )}
    </>
  );
}
